import { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from './ui/alert-dialog';
import { Copy, Check, MessageCircle, Loader2, Link2, Ban, Mail, Lock } from 'lucide-react';

const fmtDateTime = (d) => { try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

// ---- Lien de signature client ----
export function SignLinkDialog({ doc, onClose }) {
  const [link, setLink] = useState(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!doc) return;
    setLink(null); setError(''); setCopied(false); setBusy(true);
    api.post(`/documents/${doc._id}/sign-link`)
      .then(({ data }) => setLink(data))
      .catch((err) => setError(err.response?.data?.detail || 'Impossible de générer le lien'))
      .finally(() => setBusy(false));
  }, [doc]);

  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  const waText = link ? encodeURIComponent(`Bonjour ${doc?.client_name || ''}, merci de signer le document ${doc?.doc_number || ''} via ce lien sécurisé (valable 48h) : ${link.url}`) : '';

  const revoke = async () => {
    setBusy(true);
    try { await api.delete(`/documents/${doc._id}/sign-link`); setLink(null); setError('Lien révoqué. Fermez et rouvrez pour en générer un nouveau.'); }
    catch (err) { setError(err.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sign-link-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-[#0A3D73]" /> Faire signer {doc?.doc_number} par le client</DialogTitle>
          <DialogDescription>Envoyez ce lien à {doc?.client_name}. Il est à usage unique, expire dans 48 h et peut être révoqué à tout moment.</DialogDescription>
        </DialogHeader>
        {busy && !link && <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#0A3D73]" /></div>}
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="sign-link-error">{error}</p>}
        {link && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={link.url} className="font-mono text-xs border-slate-300" onFocus={(e) => e.target.select()} data-testid="sign-link-url" />
              <Button type="button" onClick={copy} variant="outline" className="border-slate-300 shrink-0" data-testid="sign-link-copy-btn">
                {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-slate-500">Expire le {fmtDateTime(link.expires_at)}</p>
            <div className="flex flex-wrap gap-2">
              <Button asChild className="bg-[#25D366] hover:bg-[#1eb85a] text-white" data-testid="sign-link-whatsapp-btn">
                <a href={`https://wa.me/${(doc?.client_phone || '').replace(/\D/g, '')}?text=${waText}`} target="_blank" rel="noopener noreferrer"><MessageCircle className="w-4 h-4 mr-2" /> Envoyer sur WhatsApp</a>
              </Button>
              <Button type="button" variant="ghost" onClick={revoke} disabled={busy} className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="sign-link-revoke-btn"><Ban className="w-4 h-4 mr-2" /> Révoquer ce lien</Button>
            </div>
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} className="border-slate-300">Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Envoi par email ----
export function EmailDialog({ doc, onClose, onSent }) {
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (doc) { setTo(doc.client_email || ''); setMessage(''); setError(''); } }, [doc]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { const { data } = await api.post(`/documents/${doc._id}/email`, { to, message }); onSent?.(data.message); onClose(); }
    catch (err) { setError(err.response?.data?.detail || 'Échec de l\'envoi'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#0A3D73]" /> Envoyer {doc?.doc_number} par email</DialogTitle>
          <DialogDescription>Le PDF {doc?.pdf_frozen ? 'signé' : 'actuel'} est joint au message.</DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <div className="space-y-1.5"><Label>Destinataire</Label><Input type="email" required value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@exemple.com" className="border-slate-300" data-testid="email-to-input" /></div>
          <div className="space-y-1.5"><Label>Message (optionnel)</Label><textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A3D73]/50 resize-none" data-testid="email-message-input" /></div>
          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="email-error">{error}</p>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-300">Annuler</Button>
            <Button type="submit" disabled={busy} className="bg-[#0A3D73] hover:bg-[#082E56] text-white" data-testid="email-send-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}Envoyer le document</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Finalisation (figer le PDF) ----
export function FinalizeDialog({ doc, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async () => {
    setBusy(true); setError('');
    try { await api.post(`/documents/${doc._id}/finalize`); onDone?.(); onClose(); }
    catch (err) { setError(err.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };
  return (
    <AlertDialog open={!!doc} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent data-testid="finalize-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5 text-[#0A3D73]" /> Finaliser {doc?.doc_number} ?</AlertDialogTitle>
          <AlertDialogDescription>Le PDF (avec cachet et signature si cochés) sera figé et scellé par une empreinte SHA-256. Le document passera en lecture seule : plus aucune modification ni suppression possible.</AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel className="border-slate-300">Annuler</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} className="bg-[#0A3D73] hover:bg-[#082E56] text-white" data-testid="finalize-confirm-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}Finaliser le document</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
